import {
  createTheme,
  type PaletteMode,
  type Theme,
  type ThemeOptions,
} from "@mui/material/styles";
import { deepmerge } from "@mui/utils";

import { palette, shape, tonal, typography } from "./index";

// The `status*` channels in `palette`. They behave like `primary`.
declare module "@mui/material/styles" {
  interface Palette {
    statusDraft: Palette["primary"];
    statusSubmitted: Palette["primary"];
    statusResubmitted: Palette["primary"];
    statusModificationRequested: Palette["primary"];
    statusModificationOpted: Palette["primary"];
    statusApproved: Palette["primary"];
    statusRejected: Palette["primary"];
    statusEscalated: Palette["primary"];
    statusPendingDeletion: Palette["primary"];
    statusPendingDeletionPublic: Palette["primary"];
  }
  interface PaletteOptions {
    statusDraft?: PaletteOptions["primary"];
    statusSubmitted?: PaletteOptions["primary"];
    statusResubmitted?: PaletteOptions["primary"];
    statusModificationRequested?: PaletteOptions["primary"];
    statusModificationOpted?: PaletteOptions["primary"];
    statusApproved?: PaletteOptions["primary"];
    statusRejected?: PaletteOptions["primary"];
    statusEscalated?: PaletteOptions["primary"];
    statusPendingDeletion?: PaletteOptions["primary"];
    statusPendingDeletionPublic?: PaletteOptions["primary"];
  }
}

// The application shell's palette (`frame` in `palette`). Not a colour channel,
// so MUI leaves it as given.
interface FrameState {
  main: string;
  contrastText: string;
}
declare module "@mui/material/styles" {
  interface Palette {
    frame: {
      bg: string;
      border: string;
      text: { primary: string; secondary: string };
      appsButton: string;
      navItem: FrameState & {
        hover: FrameState;
        selected: FrameState;
        focus: FrameState;
        pressed: FrameState;
        disabled: FrameState;
      };
    };
  }
  interface PaletteOptions {
    frame?: Palette["frame"];
  }
}

// Lets `color="statusDraft"` type-check on the status buttons and chips.
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    statusDraft: true;
    statusSubmitted: true;
    statusResubmitted: true;
    statusModificationRequested: true;
    statusModificationOpted: true;
    statusApproved: true;
    statusRejected: true;
    statusEscalated: true;
    statusPendingDeletion: true;
    statusPendingDeletionPublic: true;
  }
}
declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    statusDraft: true;
    statusSubmitted: true;
    statusResubmitted: true;
    statusModificationRequested: true;
    statusModificationOpted: true;
    statusApproved: true;
    statusRejected: true;
    statusEscalated: true;
    statusPendingDeletion: true;
    statusPendingDeletionPublic: true;
  }
}

/**
 * Outlined and text buttons drawn in a channel's `contrastText` instead of its
 * `main`, for channels whose `main` sits too close to the surface (from the
 * designer's export). With `mode`, only in that mode.
 */
const inkVariants = (
  color: "secondary" | "statusDraft",
  mode?: PaletteMode,
) => {
  const only = (theme: Theme, styles: Record<string, unknown>) =>
    mode ? theme.applyStyles(mode, styles) : styles;

  return [
    {
      props: { variant: "outlined" as const, color },
      style: ({ theme }: { theme: Theme }) =>
        only(theme, {
          color: theme.palette[color].contrastText,
          borderColor: theme.alpha(theme.palette[color].contrastText, 0.5),
          "&:hover": {
            borderColor: theme.palette[color].contrastText,
            backgroundColor: theme.alpha(
              theme.palette[color].contrastText,
              0.08,
            ),
          },
        }),
    },
    {
      props: { variant: "text" as const, color },
      style: ({ theme }: { theme: Theme }) =>
        only(theme, {
          color: theme.palette[color].contrastText,
          "&:hover": {
            backgroundColor: theme.alpha(
              theme.palette[color].contrastText,
              0.08,
            ),
          },
        }),
    },
  ];
};

/**
 * Builds the MUI v7 theme for one mode. This is the only `createTheme()` call
 * in the repo — portal and services both come through here.
 *
 * `overlay` carries app-specific chrome only: component overrides, an app's own
 * font stack, custom palette slots. **Standard palette values belong in
 * `palette` (./index.ts), not in an overlay** — that is how the two apps came
 * to disagree on `secondary` in the first place.
 *
 * The overlay is merged *before* `createTheme`, so MUI resolves one palette.
 * The standard and status channels arrive with all four of `main`, `light`,
 * `dark` and `contrastText` set, so an overlay that overrides a channel must
 * set all four: a `main` alone keeps the base shades.
 *
 * Spacing is MUI's 8px default. The 4px base this replaces only ever applied in
 * Storybook and the component tests — the apps always built at 8px, so every
 * `sx={{ p: 2 }}` in `libs/ui` was authored for 8px. The old base made Storybook
 * preview these components at half their real spacing.
 */
export const buildMuiTheme = (
  mode: PaletteMode = "light",
  overlay?: ThemeOptions,
): Theme => {
  const base: ThemeOptions = {
    palette: { mode, ...tonal, ...palette[mode] },
    shape,
    typography: {
      fontFamily: typography.fontFamily,
      fontWeightRegular: typography.weights.regular,
      fontWeightMedium: typography.weights.medium,
      fontWeightBold: typography.weights.bold,
    },
    components: {
      // Mobile tap targets: MUI's defaults (~36-40px) fall short of the
      // ~48px minimum tap-target guidance. Desktop keeps its current sizing.
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            [theme.breakpoints.down("sm")]: {
              minWidth: 48,
              minHeight: 48,
            },
          }),
        },
        // Dark `secondary` and both modes' `statusDraft` sit close to their
        // surfaces.
        variants: [
          ...inkVariants("secondary", "dark"),
          ...inkVariants("statusDraft"),
        ],
      },
      MuiAlert: {
        styleOverrides: {
          // MUI colours filled alerts with getContrastText(main) and never
          // reads contrastText, which would override the designer's choice.
          // Alert has no per-severity slot, so the colour comes off ownerState.
          filled: ({ ownerState, theme }) => {
            const key = ownerState.color ?? ownerState.severity;
            const channel = key ? theme.palette[key] : undefined;
            return channel ? { color: channel.contrastText } : {};
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            [theme.breakpoints.down("sm")]: {
              minWidth: 48,
              minHeight: 48,
            },
          }),
        },
      },
    },
  };

  return createTheme(deepmerge(base, overlay ?? {}));
};
